/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { useDispatch } from 'react-redux';
import { colors } from '@blend/consumer-components';

import { State } from '../../redux';
import { ConnectedTaskForm } from '../../components/form/ConnectedTaskForm';
import { useFetchCurrentTask } from '../../hooks/useFetchCurrentTask';
import { mediaBreakpoints } from '../../common/responsive';
import { ExitSessionConfirmationModal } from '../../components/ParticipantRoom/ExitSessionConfirmationModal/ExitSessionConfirmationModal';
import { useRootSelector } from '../../hooks/useRootSelector';
import { useRoom } from '../../hooks/useRoom';
import { Spinner } from './Spinner';
import { Footer } from './Footer/Footer';
import { setRumSessionUserName } from '@utils/datadogRum';
import { onboardingPerformanceTracker } from '@utils/onboardingPerformanceTracker';

const styles = StyleSheet.create({
  tasksPageWrapper: {
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
  },
  content: {
    flex: 1,
    backgroundColor: colors.white,
    width: 520,
    [mediaBreakpoints.mobile]: {
      width: '100%',
    },
  },
});

const secondaryCTAButtonTaskTypes = ['waitingRoom'];

export namespace TasksPage {
  export interface OwnProps {}
  export interface StateProps {
    task: State['tasks']['current'];
  }
  export type Props = StateProps & OwnProps;
}

export const TasksPage: React.FC<TasksPage.Props> = () => {
  const dispatch = useDispatch();
  const { current: task } = useRootSelector(state => state.tasks);
  const isLoading = useFetchCurrentTask(dispatch);
  const { roomId } = useRoom();

  setRumSessionUserName(roomId, 'participant-onboarding');

  // Track task loading and step transitions
  useEffect(() => {
    if (task && roomId) {
      onboardingPerformanceTracker.startStep(task.id);
    }
  }, [task, roomId]);

  if (isLoading) {
    return <Spinner />;
  }

  window.scrollTo(0, 0); // needed for mobile landscape

  const showBackButton = task?.template?.customProps?.showBackToUserSelectButton;
  const hideMessage = task?.template?.customProps?.hideMessage;
  const ctaButtonStyle = secondaryCTAButtonTaskTypes.includes(task?.template?.type) ? 'secondary' : 'primary';

  return (
    <div className={css(styles.tasksPageWrapper)} data-it="TasksPage">
      <div className={css(styles.content)}>
        <ConnectedTaskForm showBackButton={showBackButton} hideMessage={hideMessage} ctaButtonStyle={ctaButtonStyle} />
      </div>
      <Footer />
      <ExitSessionConfirmationModal />
    </div>
  );
};
